import React, { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Col, FormGroup, Input, Label, Row } from "reactstrap";
import { Breadcrumb, BreadcrumbItem } from "reactstrap";
import { Fade } from "react-reveal";
import { Link, useNavigate, useParams } from "react-router-dom";
import { catchAsync, checkResponse } from "utilities/utilities";
import { ADD_APP_INTRO, ADD_TALENT, APP_INTRO_DETAILS, MANAGE_APP_INTRO, TALENT_DETAILS, UPLOAD_FILE } from "services/ApiCalls";
import Loading from "Component/common/Loading";
import fileUploader from "utilities/fileUploader";

// VALIDATION SCHEMA
const schema = z
  .object({
    short_description: z.string().min(1, "Short description is required"),
    description: z.string().min(1, "Description is required"),
    media_type: z.string().min(1, "Media type is required"),
    media: z.string().optional(), // base optional
  })
  .refine(
    (data) =>
      // If media_type exists → media must not be empty
      !(data.media_type && !data.media),
    {
      path: ["media"],
      message: "Media file is required",
    }
  );

const AddIntro = () => {
  const [submitLoader, setSubmitLoader] = useState(false);
  const [imageUploadLoading, setImageUploadLoading] = useState(false);
  const [media, setMedia] = useState("");

  const navigate = useNavigate();
  const { id } = useParams();
  const EditId = !!id;

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      short_description: "",
      description: "",
      media_type: "",
      media: "",
    },
  });

  // IMAGE / VIDEO UPLOADER
  const onMediaUpload = async (e) => {
    const file = e.target.files?.[0];
    console.log(file , "file");
    if (!file) return;

    setImageUploadLoading(true);
    const formData = new FormData();
    formData.append("files", file);
    const res = await UPLOAD_FILE(formData);
    console.log(res , "res");
    if (res?.status === 200) {
      setMedia(res?.data?.data?.files[0]);
      setValue("media", res?.data?.data?.files[0]);
      setImageUploadLoading(false);
    } else {
      setImageUploadLoading(false);
    }
  };

  useEffect(() => {
  
      setValue("media", media);
      console.log(media);
    
  }, [media]);

  // SUBMIT HANDLER
  const submitHandler = async (data) => {
    setSubmitLoader(true);

    const requestBody = {
      _id: id || undefined,
      short_description: data.short_description,
      description: data.description,
      media_type: data.media_type,
      media: data.media,
    };
    

  let res = id ? await MANAGE_APP_INTRO(requestBody) : await ADD_APP_INTRO(requestBody);

    checkResponse({
      res,
      showSuccess: true,
      navigateUrl: "/app-intro",
      navigate,
      setLoader: setSubmitLoader,
    });
  };

  // FETCH DATA FOR EDIT MODE
  const getData = catchAsync(async () => {
    const res = await APP_INTRO_DETAILS(id);

    checkResponse({
      res,
      setData: (data) => {
        reset({
          short_description: data.short_description || "",
          description: data.description || "",
          media_type: data.media_type || "",
          media: data.media || "",
        });

        setMedia(data.media || "");
      },
    });
  });

  useEffect(() => {
    if (id) getData();
  }, [id]);

  return (
    <Fade>
      <section className="addresources comon_dashpad">
        <form onSubmit={handleSubmit(submitHandler)}>
          <div className="comom_fillter_set text-start">

            {/* HEADER */}
            <div className="resource_headwrap d-flex align-items-center justify-content-between">
              <h5 className="fillter_head">
                {EditId ? "Edit Intro" : "Add Intro"}
              </h5>
            </div>

            {/* BREADCRUMB */}
            <div className="breadcrumb_set mt-3">
              <Breadcrumb>
                <BreadcrumbItem>
                  <Link to="/app-intro">Manage Intro</Link>
                </BreadcrumbItem>
                <BreadcrumbItem active>
                  {EditId ? "Edit Intro" : "Add Intro"}
                </BreadcrumbItem>
              </Breadcrumb>
            </div>

            {/* FORM CARD */}
            <div className="addresources_bottom_form rounded-3 p-3">
              <div className="author_form">
                <Row>

                  {/* SHORT DESCRIPTION */}
                  <Col lg={6}>
                    <FormGroup>
                      <Label>Short Description</Label>
                      <Controller
                        name="short_description"
                        control={control}
                        render={({ field }) => (
                          <Input {...field} style={{ color: "white" }} />
                        )}
                      />
                      {errors.short_description && (
                        <span style={{ color: "red" }}>
                          {errors.short_description.message}
                        </span>
                      )}
                    </FormGroup>
                  </Col>

                  {/* DESCRIPTION */}
                  <Col lg={6}>
                    <FormGroup>
                      <Label>Description</Label>
                      <Controller
                        name="description"
                        control={control}
                        render={({ field }) => (
                          <Input
                            type="textarea"
                            {...field}
                            style={{ color: "white" }}
                          />
                        )}
                      />
                      {errors.description && (
                        <span style={{ color: "red" }}>
                          {errors.description.message}
                        </span>
                      )}
                    </FormGroup>
                  </Col>

                  {/* MEDIA TYPE SELECT */}
                  <Col lg={6}>
                    <FormGroup>
                      <Label>Media Type</Label>
                      <Controller
                        name="media_type"
                        control={control}
                        render={({ field }) => (
                          <Input
                            type="select"
                            {...field}
                            style={{ color: "white" }}
                            onChange={(e) => {
                              field.onChange(e);
                              setMedia("");
                              setValue("media", "");
                            }}
                          >
                            <option value="">Select Type</option>
                            <option value="image">Image</option>
                            <option value="video">Video</option>
                          </Input>
                        )}
                      />
                      {errors.media_type && (
                        <span style={{ color: "red" }}>
                          {errors.media_type.message}
                        </span>
                      )}
                    </FormGroup>
                  </Col>

                  {/* MEDIA UPLOAD (Conditional) */}
                  <Col lg={6}>
  <FormGroup>
    {watch("media_type") && (
      <>
        <Label>
          Upload {watch("media_type") === "image" ? "Image" : "Video"}
        </Label>

        <Input
          type="file"
          accept={watch("media_type") === "image" ? "image/*" : "video/*"}
          onChange={onMediaUpload}
        />
        {errors.media && (
          <span style={{ color: "red" }}>
            {errors.media.message}
          </span>
        )}

        {imageUploadLoading && <Loading />}

        {media && (
          <div className="mt-2 position-relative" style={{ display: "inline-block" }}>
            {watch("media_type") === "image" ? (
              <img
                src={media}
                alt="Uploaded"
                style={{ maxWidth: "20%", borderRadius: 5 }}
              />
            ) : watch("media_type") === "video" ? (
              <video
                src={media}
                controls
                style={{ maxWidth: "20%", borderRadius: 5 }}
              />
            ) : null}

            {/* CROSS BUTTON */}
            <button
              type="button"
              onClick={() => {
                setMedia("");
                setValue("media", "");
              }}
              style={{
                position: "absolute",
                top: -7,
                right: "75%",
                background: "red",
                color: "white",
                border: "none",
                borderRadius: "50%",
                width: 25,
                height: 25,
                cursor: "pointer",
                fontWeight: "bold",
              }}
            >
              ×
            </button>
          </div>
        )}
      </>
    )}
  </FormGroup>
</Col>

                  {/* SUBMIT BUTTON */}
                  <Col lg={6} className="mt-4">
                    <Button className="export_csv w-100" style={{ height: 50 }}>
                      {submitLoader ? <Loading fullSize /> : "Submit"}
                    </Button>
                  </Col>

                </Row>
              </div>
            </div>

          </div>
        </form>
      </section>
    </Fade>
  );
};

export default AddIntro;
